/**
 * Cart checkout: due date, minimum quantity, optional rush (12–14 days).
 * Does not block checkout based on product handle — FPD line items are not "stoles".
 */
(function () {
  const ATTRIBUTE_KEY = 'Date stoles are due';
  const RUSH_ATTRIBUTE_KEY = 'Rush production';
  const BULK_ATTRIBUTE_KEY = 'Bulk discount';
  const MIN_ORDER_QTY = window.gradStolesCartConfig?.minOrderQty ?? 6;
  const MIN_LEAD_DAYS = window.gradStolesCartConfig?.minLeadDays ?? 12;
  const STANDARD_LEAD_DAYS = window.gradStolesCartConfig?.standardLeadDays ?? 15;
  const RUSH_FEE = window.gradStolesCartConfig?.rushFee ?? 50;

  let submittingCheckout = false;
  let listenersBound = false;

  function getDueDateInputs() {
    return Array.from(document.querySelectorAll('[data-cart-due-date]'));
  }

  function formatLocalDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function parseDateValue(value) {
    if (!value) return '';
    const trimmed = String(value).trim();
    const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usMatch) {
      return `${usMatch[3]}-${usMatch[1].padStart(2, '0')}-${usMatch[2].padStart(2, '0')}`;
    }
    return trimmed;
  }

  function getMinDueDateString() {
    const minDate = new Date();
    minDate.setHours(0, 0, 0, 0);
    minDate.setDate(minDate.getDate() + MIN_LEAD_DAYS);
    return formatLocalDate(minDate);
  }

  function daysFromToday(dateStr) {
    const normalized = parseDateValue(dateStr);
    const parts = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!parts) return null;
    const selected = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selected.setHours(0, 0, 0, 0);
    return Math.round((selected.getTime() - today.getTime()) / 86400000);
  }

  function isRushDate(dateStr) {
    const days = daysFromToday(dateStr);
    return days !== null && days >= MIN_LEAD_DAYS && days < STANDARD_LEAD_DAYS;
  }

  function pickDueDateInput() {
    const inputs = getDueDateInputs();
    if (!inputs.length) return null;
    const filledVisible = inputs.find((input) => input.value && input.getClientRects().length > 0);
    if (filledVisible) return filledVisible;
    const visible = inputs.find((input) => input.getClientRects().length > 0);
    if (visible) return visible;
    return inputs.find((input) => input.value) || inputs[0];
  }

  function setInputConstraints(input) {
    if (!input) return;
    input.min = getMinDueDateString();
    input.required = true;
  }

  function dueDateRequiredMessage() {
    return (
      window.gradStolesCartConfig?.dueDateRequiredError ??
      'Please enter the date your stoles are due before checking out.'
    );
  }

  function dueDateLeadTimeMessage() {
    return (
      window.gradStolesCartConfig?.dueDateLeadTimeError ??
      `That date is too soon. The earliest date is ${MIN_LEAD_DAYS} days from today (rush, $${RUSH_FEE}). Standard production is 2–3 weeks.`
    );
  }

  function showFieldError(input, message) {
    const field = input?.closest('.cart-due-date');
    const errorEl = field?.querySelector('.cart-due-date__error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
    if (input) {
      input.setAttribute('aria-invalid', 'true');
      try {
        input.focus();
      } catch (e) {
        /* ignore */
      }
    }
  }

  function clearFieldError(input) {
    const field = input?.closest('.cart-due-date');
    const errorEl = field?.querySelector('.cart-due-date__error');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
    input?.removeAttribute('aria-invalid');
  }

  function showCartError(message) {
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) {
      errors.textContent = message;
      errors.hidden = false;
    }
  }

  function clearCartError() {
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = '';
  }

  function updateRushNotice(dateStr) {
    const notices = document.querySelectorAll('[data-cart-rush-notice]');
    const rush = Boolean(dateStr && isRushDate(dateStr));
    notices.forEach((notice) => {
      notice.hidden = !rush;
    });
  }

  function getDueDateValue(input, cart) {
    const fromInputs = getDueDateInputs()
      .map((el) => parseDateValue(el.value))
      .find(Boolean);
    if (fromInputs) return fromInputs;
    const inputValue = parseDateValue(input?.value);
    if (inputValue) return inputValue;
    return parseDateValue(cart?.attributes?.[ATTRIBUTE_KEY] || '');
  }

  function validateDueDateValue(value, input) {
    if (input) clearFieldError(input);

    if (!value) {
      const message = dueDateRequiredMessage();
      showFieldError(input, message);
      showCartError(message);
      return false;
    }

    const days = daysFromToday(value);
    if (days === null || days < MIN_LEAD_DAYS) {
      const message = dueDateLeadTimeMessage();
      showFieldError(input, message);
      showCartError(message);
      updateRushNotice('');
      return false;
    }

    updateRushNotice(value);
    clearCartError();
    return true;
  }

  function bulkDiscountForQty(qty) {
    if (qty >= 150) return '25% off (150+ stoles)';
    if (qty >= 100) return '20% off (100–149 stoles)';
    if (qty >= 50) return '15% off (50–99 stoles)';
    if (qty >= 10) return '10% off (10–49 stoles)';
    return 'None — add 10+ stoles for 10% off';
  }

  async function saveCartAttributes(dueDateValue, itemCount) {
    if (!window.routes?.cart_update_url || !dueDateValue) return false;
    const rush = isRushDate(dueDateValue);
    try {
      const response = await fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            [ATTRIBUTE_KEY]: dueDateValue,
            [RUSH_ATTRIBUTE_KEY]: rush ? `Yes — $${RUSH_FEE}` : 'No',
            [BULK_ATTRIBUTE_KEY]: bulkDiscountForQty(itemCount || 0),
          },
        }),
      });
      return response.ok;
    } catch (e) {
      return false;
    }
  }

  async function getCart() {
    if (!window.routes?.cart_url) return { item_count: 0, items: [], attributes: {} };
    try {
      const response = await fetch(`${window.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return { item_count: 0, items: [], attributes: {} };
      return response.json();
    } catch (e) {
      return { item_count: 0, items: [], attributes: {} };
    }
  }

  function goToCheckout() {
    window.location.assign('/checkout');
  }

  function isCheckoutTrigger(target) {
    if (!(target instanceof Element)) return { checkoutButton: null, acceleratedCheckout: null };
    const checkoutButton = target.closest('#checkout, #CartDrawer-Checkout, [data-gradstoles-checkout-trigger]');
    const acceleratedCheckout = target.closest(
      '.additional-checkout-buttons, [data-shopify-buttoncontainer], shopify-accelerated-checkout-cart, shopify-accelerated-checkout'
    );
    return { checkoutButton, acceleratedCheckout };
  }

  async function validateCheckout(event) {
    let checkoutButton = null;
    let acceleratedCheckout = null;

    if (event.type === 'submit') {
      const form = event.target;
      if (!form || (form.id !== 'cart' && form.id !== 'CartDrawer-Form')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      checkoutButton =
        document.getElementById('checkout') ||
        document.getElementById('CartDrawer-Checkout') ||
        form.querySelector('[data-gradstoles-checkout-trigger]');
    } else {
      const trigger = isCheckoutTrigger(event.target);
      checkoutButton = trigger.checkoutButton;
      acceleratedCheckout = trigger.acceleratedCheckout;
      if (!checkoutButton && !acceleratedCheckout) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if (checkoutButton?.disabled) return;
    if (submittingCheckout) return;
    submittingCheckout = true;
    clearCartError();

    try {
      if (acceleratedCheckout) {
        showCartError(
          window.gradStolesCartConfig?.useCheckoutButtonError ??
            'Please use Check out so we can collect the date your stoles are due.'
        );
        return;
      }

      const cart = await getCart();
      const qty = cart.item_count || 0;

      if (qty > 0 && qty < MIN_ORDER_QTY) {
        showCartError(
          window.gradStolesCartConfig?.minQtyError ??
            `Minimum order is ${MIN_ORDER_QTY} stoles. Please update your quantity before checkout.`
        );
        return;
      }

      const primaryInput = pickDueDateInput();
      if (primaryInput) setInputConstraints(primaryInput);

      const dueDateValue = getDueDateValue(primaryInput, cart);
      if (primaryInput && dueDateValue && primaryInput.value !== dueDateValue) {
        primaryInput.value = dueDateValue;
      }

      if (qty > 0 && !validateDueDateValue(dueDateValue, primaryInput)) return;

      if (checkoutButton) checkoutButton.setAttribute('aria-busy', 'true');
      await saveCartAttributes(dueDateValue, qty);
      goToCheckout();
    } catch (e) {
      showCartError('We could not start checkout. Please try again.');
    } finally {
      if (checkoutButton) checkoutButton.removeAttribute('aria-busy');
      window.setTimeout(() => {
        submittingCheckout = false;
      }, 1500);
    }
  }

  function onDueDateChange(input) {
    setInputConstraints(input);
    if (!input.value) {
      clearFieldError(input);
      updateRushNotice('');
      return;
    }
    if (!validateDueDateValue(parseDateValue(input.value), input)) return;
    getCart().then((cart) => saveCartAttributes(parseDateValue(input.value), cart.item_count));
  }

  function initDueDateFields() {
    getDueDateInputs().forEach((input) => {
      setInputConstraints(input);
      if (input.value) updateRushNotice(parseDateValue(input.value));

      if (input.dataset.gradstolesBound === 'true') return;
      input.dataset.gradstolesBound = 'true';
      input.addEventListener('change', () => onDueDateChange(input));
      input.addEventListener('blur', () => {
        if (input.value) onDueDateChange(input);
      });
      input.addEventListener('input', () => {
        if (!input.value) {
          clearFieldError(input);
          updateRushNotice('');
        }
      });
    });
  }

  function bindListenersOnce() {
    if (listenersBound) return;
    listenersBound = true;
    document.addEventListener('click', validateCheckout, true);
    document.addEventListener('submit', validateCheckout, true);
  }

  function init() {
    bindListenersOnce();
    initDueDateFields();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      window.setTimeout(initDueDateFields, 50);
    });
  }
})();
